import { z } from 'zod';

import { VariantFulfillmentMode, VariantStatus } from '@nucleus-commerce/api-client/admin';

import { MAX_LEAD_TIME_DAYS } from './product-form-schema';
import { weightFieldSchema } from './weight-schema';

const DECIMAL = /^\d+(\.\d+)?$/;

const optionalDecimal = (message: string) =>
  z
    .string()
    .trim()
    .refine((v) => v === '' || DECIMAL.test(v), { error: message });

const dayCount = z
  .string()
  .trim()
  .refine((v) => v === '' || /^\d{1,3}$/.test(v), { error: 'products:validation.integer' })
  .refine((v) => v === '' || Number(v) <= MAX_LEAD_TIME_DAYS, {
    error: 'products:validation.leadTimeMax',
  });

const money = z.object({ amount: z.number().int(), currency: z.string() });

/** `UpdateVariantBody` as the detail sheet edits it; blanks mean null, "inherit" mode means null. */
export const variantFormSchema = z
  .object({
    sku: z.string().trim().max(64, { error: 'products:validation.skuLength' }),
    barcode: z.string().trim().max(64, { error: 'products:validation.barcodeLength' }),
    price: money
      .nullable()
      .refine((v) => v !== null, { error: 'products:validation.priceRequired' }),
    compareAt: money.nullable(),
    cost: money.nullable(),
    weight: weightFieldSchema,
    lengthMm: optionalDecimal('products:validation.dimension'),
    widthMm: optionalDecimal('products:validation.dimension'),
    heightMm: optionalDecimal('products:validation.dimension'),
    requiresShipping: z.boolean(),
    taxable: z.boolean(),
    taxCode: z.string().trim().max(40, { error: 'products:validation.taxCodeLength' }),
    trackInventory: z.boolean(),
    allowBackorder: z.boolean(),
    /** Empty string inherits the product's mode. */
    fulfillmentMode: z.enum(VariantFulfillmentMode).or(z.literal('')),
    leadTimeDaysMin: dayCount,
    leadTimeDaysMax: dayCount,
    status: z.enum(VariantStatus),
  })
  .refine(
    (v) =>
      v.leadTimeDaysMin === '' ||
      v.leadTimeDaysMax === '' ||
      Number(v.leadTimeDaysMin) <= Number(v.leadTimeDaysMax),
    { path: ['leadTimeDaysMax'], error: 'products:validation.leadTimeOrder' },
  )
  .refine(
    (v) => {
      const given = [v.lengthMm, v.widthMm, v.heightMm].filter((d) => d !== '').length;
      return given === 0 || given === 3;
    },
    { path: ['heightMm'], error: 'products:validation.dimensionsAllOrNone' },
  );

export type VariantFormInput = z.input<typeof variantFormSchema>;
export type VariantFormOutput = z.output<typeof variantFormSchema>;

/** A new variant: its option values plus the few fields worth asking for up front. */
export const newVariantFormSchema = z.object({
  optionValues: z.record(
    z.string(),
    z.string().min(1, { error: 'products:validation.optionValueRequired' }),
  ),
  sku: z.string().trim().max(64, { error: 'products:validation.skuLength' }),
  price: money.nullable().refine((v) => v !== null, { error: 'products:validation.priceRequired' }),
  compareAt: money.nullable(),
});

export type NewVariantFormInput = z.input<typeof newVariantFormSchema>;
export type NewVariantFormOutput = z.output<typeof newVariantFormSchema>;
